// ENG-22245: Sentry's rage/dead-click detection hardcodes an attribute allowlist
// (id, class, aria-label, role, name, alt, title, data-test-id, data-testid,
// disabled, aria-disabled, data-sentry-component) with no supported way to narrow
// it via Sentry.init()/replayIntegration() options, and its own masking only
// covers the "Clicked Element" attribute summary — not the raw "Selector Path"
// breadcrumb message (htmlTreeAsString output) — so a PII-bearing aria-label,
// title, alt, or name value leaked there unmasked. This scrubs both down to an
// explicit safe subset before the breadcrumb ever leaves the browser.

// Keys as they appear on the click-target node object Sentry's own
// getAttributesToRecord() builds (data-testid/data-test-id are normalized to
// testId there).
const SAFE_NODE_ATTRIBUTES = new Set(["id", "class", "role", "testId", "data-sentry-component"]);

// Attribute names as they appear literally inside the CSS-selector-like
// breadcrumb message (htmlTreeAsString output) — raw HTML attribute names, not
// the normalized node.attributes keys above.
const SAFE_SELECTOR_ATTRIBUTES = new Set([
  "id",
  "class",
  "role",
  "data-testid",
  "data-test-id",
  "data-sentry-component",
]);

const SELECTOR_ATTRIBUTE_PATTERN = /\[([\w-]+)="[^"]*"\]/g;

export function scrubSelectorPath(selector: string): string {
  return selector.replace(SELECTOR_ATTRIBUTE_PATTERN, (match, attributeName) =>
    SAFE_SELECTOR_ATTRIBUTES.has(attributeName) ? match : "",
  );
}

interface ClickTargetNode {
  textContent?: unknown;
  attributes?: Record<string, unknown>;
}

export function scrubClickTargetNode(node: unknown): void {
  if (!node || typeof node !== "object") {
    return;
  }

  const target = node as ClickTargetNode;
  delete target.textContent;

  if (!target.attributes) {
    return;
  }

  for (const attributeName of Object.keys(target.attributes)) {
    if (!SAFE_NODE_ATTRIBUTES.has(attributeName)) {
      delete target.attributes[attributeName];
    }
  }
}

interface UiBreadcrumbPayload {
  category?: unknown;
  message?: unknown;
  data?: { node?: unknown };
}

export function scrubUiBreadcrumbPayload(payload: unknown): void {
  if (!payload || typeof payload !== "object") {
    return;
  }

  const breadcrumb = payload as UiBreadcrumbPayload;
  if (typeof breadcrumb.category !== "string" || !breadcrumb.category.startsWith("ui.")) {
    return;
  }

  if (typeof breadcrumb.message === "string") {
    breadcrumb.message = scrubSelectorPath(breadcrumb.message);
  }

  scrubClickTargetNode(breadcrumb.data?.node);
}
